import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Bell, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationPreferences({ user, logout }) {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    game_scheduled: true,
    standby_promoted: true,
    join_request_approved: true,
    game_cancelled: true,
    game_updated: true,
    team_announcement: true,
    event_reminder: true,
    general: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamMutes, setTeamMutes] = useState([]);

  useEffect(() => {
    fetchPreferences();
    fetchTeamMutes();
  }, []);

  const fetchTeamMutes = async () => {
    try {
      const r = await axios.get(`${API}/section/whatsapp/my-team-mutes`,
        { withCredentials: true });
      setTeamMutes(r.data.teams || []);
    } catch (e) {
      setTeamMutes([]);
    }
  };

  const toggleTeamMute = async (team) => {
    try {
      const r = await axios.put(`${API}/section/whatsapp/my-team-mutes`,
        { team_id: team.team_id, muted: !team.muted }, { withCredentials: true });
      setTeamMutes(r.data.teams || []);
      toast.success(team.muted
        ? `${team.name} messages back on`
        : `${team.name} messages muted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change that");
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        withCredentials: true
      });
      setPreferences({
        game_scheduled: response.data.game_scheduled,
        standby_promoted: response.data.standby_promoted,
        join_request_approved: response.data.join_request_approved,
        game_cancelled: response.data.game_cancelled,
        game_updated: response.data.game_updated,
        team_announcement: response.data.team_announcement,
        event_reminder: response.data.event_reminder,
        general: response.data.general
      });
    } catch (error) {
      console.error("Error fetching preferences:", error);
      toast.error("Failed to load preferences");
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/notifications/preferences`,
        preferences,
        { withCredentials: true }
      );
      toast.success("Notification preferences saved");
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const notificationTypes = [
    {
      key: "game_scheduled",
      title: "New Game Scheduled",
      description: "Get notified when a new game is created for your team",
      icon: "⚽"
    },
    {
      key: "standby_promoted",
      title: "Standby Promotion",
      description: "Get notified when you're promoted from standby to the squad",
      icon: "🎉"
    },
    {
      key: "join_request_approved",
      title: "Join Request Approved",
      description: "Get notified when your team join request is approved",
      icon: "✅"
    },
    {
      key: "game_cancelled",
      title: "Game Cancelled",
      description: "Get notified when a game you're in is cancelled",
      icon: "❌"
    },
    {
      key: "game_updated",
      title: "Game Updated",
      description: "Get notified when game details are changed (time, location, etc.)",
      icon: "📝"
    },
    {
      key: "team_announcement",
      title: "Team Announcements",
      description: "Get notified about important team announcements",
      icon: "📢"
    },
    {
      key: "event_reminder",
      title: "Event Reminders",
      description: "Get reminded about upcoming events you're registered for",
      icon: "🔔"
    },
    {
      key: "general",
      title: "General Notifications",
      description: "Get other important notifications and updates",
      icon: "📬"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/notifications")}
            variant="ghost"
            className="text-yellow-500 hover:text-yellow-500"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Notifications
          </Button>

          <Button
            onClick={savePreferences}
            disabled={saving}
            className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Bell className="w-8 h-8 text-yellow-500" />
            Notification Preferences
          </h1>
          <p className="text-slate-400">
            Choose which notifications you'd like to receive
          </p>
        </div>

        {/* Preferences List */}
        <Card className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg">
          <div className="space-y-4">
            {notificationTypes.map((type) => (
              <div
                key={type.key}
                className="flex items-start gap-4 p-4 bg-[#0a0f1a] rounded-lg border border-white/5"
              >
                <div className="text-2xl flex-shrink-0">
                  {type.icon}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white mb-1">
                    {type.title}
                  </h3>
                  <p className="text-sm text-slate-400">
                    {type.description}
                  </p>
                </div>

                <button
                  onClick={() => togglePreference(type.key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences[type.key]
                      ? "bg-yellow-500/100"
                      : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-slate-900/50 transition-transform ${
                      preferences[type.key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-slate-300">
              <strong>Note:</strong> These preferences only apply to push notifications. 
              You'll still see important notifications in your notification inbox.
            </p>
          </div>
        </Card>

        {teamMutes.length > 0 && (
          <Card className="mt-6 p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg"
            data-testid="team-mutes-card">
            <h2 className="text-xl font-semibold text-white mb-1">WhatsApp from your teams</h2>
            <p className="text-sm text-slate-400 mb-4">
              Mute a team to stop its game messages while you keep hearing from your others.
              Replying STOP on WhatsApp silences every team at once, so use this instead.
            </p>
            <div className="space-y-3">
              {teamMutes.map((team) => (
                <div key={team.team_id}
                  className="flex items-center gap-4 p-4 bg-[#0a0f1a] rounded-lg border border-white/5"
                  data-testid={`team-mute-row-${team.team_id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white">{team.name}</h3>
                    <p className="text-sm text-slate-400">
                      {team.muted ? "Muted — no game messages" : "Game messages on"}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleTeamMute(team)}
                    aria-label={`Toggle ${team.name} WhatsApp`}
                    data-testid={`team-mute-toggle-${team.team_id}`}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      team.muted ? "bg-gray-300" : "bg-yellow-500/100"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-slate-900/50 transition-transform ${
                        team.muted ? "translate-x-1" : "translate-x-6"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
